import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButton,
  IonIcon,
  IonLabel,
  IonSkeletonText,
  IonList,
  IonItem,
  IonThumbnail,
  IonCard,
  IonCardHeader,
  IonCardContent,
  IonBadge,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonImg,
  useIonToast,
  useIonAlert,
  useIonModal,
} from "@ionic/react";
import React, { useState } from "react";
import {
  storefrontOutline,
  arrowBackOutline,
  warningOutline,
  chevronDownCircleOutline,
  calendar,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";

import { RestApi } from "../services/Api";
import { loadList } from "../models/Data";
import { getNameDate, getCollectByCustomer, Rupiah, sumByCustomerKey, useConstructor } from "../services/Library";

import Detail from "../components/bill/Detail";
import Calendar from "../components/bill/Calendar";

import "./Bill.scss";

/* =======================
   Component
======================= */

const Bill: React.FC = () => {
  const { t } = useTranslation(["general"]);
  const [presentToast] = useIonToast();
  const [alert] = useIonAlert();

  const [loaded, setLoaded] = useState(false);
  const [bill, setBill] = useState<any[]>([]);
  const [detailBill, setDetailBill] = useState<any | null>(null);

  /* =======================
     API
  ======================= */

  const getBillList = async () => {
    try {
      const res: any = await RestApi("bill", "get", {}, "application/json");

      if (res?.data?.status) {
        setBill(res.data.data ?? []);
      } else {
        setBill([]);
        presentToast({
          message: res?.data?.message ?? "Error",
          duration: 2500,
          color: "danger",
          icon: warningOutline,
        });
      }
    } catch (err: any) {
      alert({
        header: t("error"),
        message: err.message,
        buttons: ["OK"],
      });
    } finally {
      setLoaded(true);
    }
  };

  /* =======================
     Modal
  ======================= */

  const [presentDetail, dismissDetail] = useIonModal(Detail, {
    detailBill,
    type: "bill",
    onDismissDetail: () => dismissDetail(),
  });

  const [presentCalendar, dismissCalendar] = useIonModal(Calendar, {
    onDismissCalendar: () => dismissCalendar(),
  });

  /* =======================
     Lifecycle
  ======================= */

  useConstructor(() => {
    getBillList();
  });

  /* =======================
     Render
  ======================= */

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {!loaded ? (
            <>
              <IonSkeletonText animated style={{ width: "100%" }} />
              <IonSkeletonText animated style={{ width: "100%" }} />
            </>
          ) : (
            <>
              <IonButton
                slot="start"
                fill="clear"
                color="light"
                routerLink="/home"
                routerDirection="back"
              >
                <IonIcon src={arrowBackOutline} />
              </IonButton>

              <IonLabel className="ion-text-center" color="light">
                <h1>
                  <strong>{getNameDate(bill[0]?.bill_date)}</strong>
                </h1>
              </IonLabel>

              <IonButton
                slot="end"
                fill="clear"
                color="light"
                onClick={() =>
                  presentCalendar({ cssClass: "modal-fullscreen" })
                }
              >
                <IonIcon src={calendar} />
              </IonButton>
            </>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent className="bill-content">
        {!loaded && (
          <IonList>
            {loadList.map((_, i) => (
              <IonItem key={i}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated />
                </IonThumbnail>
                <IonLabel>
                  <IonSkeletonText animated style={{ width: "80%" }} />
                  <IonSkeletonText animated style={{ width: "60%" }} />
                  <IonSkeletonText animated style={{ width: "30%" }} />
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => {
            getBillList();
            e.detail.complete();
          }}
        >
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            refreshingSpinner="circles"
            pullingText={t("loading") ?? ""}
            refreshingText={t("loading") ?? ""}
          />
        </IonRefresher>

        {loaded && bill.length > 0 ? (
          <IonList>
            {bill.map((item: any, i: number) => {
              const billdet = item.billdet ?? [];
              return (
                <IonCard
                  key={i}
                  className="ion-padding"
                  onClick={() => {
                    setDetailBill(item);
                    presentDetail({
                      cssClass: "modal-fullscreen",
                    });
                  }}
                >
                  <IonCardHeader className="ion-no-padding ion-no-margin">
                    <IonItem className="ion-no-padding ion-no-margin">
                      <IonIcon src={storefrontOutline} />
                      <IonLabel className="ion-no-padding ion-no-margin">
                        <strong>
                          {item.customer_name} ({item.customer_id})
                        </strong>
                      </IonLabel>
                      <IonText
                        slot="end"
                        className="ion-text-end ion-no-padding ion-no-margin"
                      >
                        <IonBadge color="dark">
                          {t("collect")} :{" "}
                          {Rupiah(
                            getCollectByCustomer(billdet, item.customer_id)
                          )}
                        </IonBadge>{" "}
                        <br />
                        {item.bill_id}
                      </IonText>
                    </IonItem>
                  </IonCardHeader>

                  <IonCardContent className="ion-no-padding ion-no-margin">
                    <IonItem className="ion-no-padding ion-no-margin">
                      <IonLabel>
                        <p>
                          {item.customer_address_name}, {item.customer_address}
                        </p>
                      </IonLabel>
                      <IonLabel slot="end">
                        <p>{item.customer_phone}</p>
                      </IonLabel>
                    </IonItem>

                    <IonItem className="ion-no-padding ion-no-margin">
                      <IonText>
                        <p>
                          Total {t("invoice")} <br />
                          {Rupiah(
                            sumByCustomerKey(
                              item.customer_id,
                              billdet,
                              "billdet_si_total_amount"
                            )
                          )}
                        </p>
                        <p>
                          Jumlah {t("invoice")} <br />
                          {
                            billdet.filter(
                              (x: any) => x.si_customer_id === item.customer_id
                            ).length
                          }
                        </p>
                      </IonText>

                      <IonText slot="end" className="ion-text-end">
                        <p>
                          Sisa {t("owing")} <br />
                          {Rupiah(
                            sumByCustomerKey(
                              item.customer_id,
                              billdet,
                              "billdet_si_total_amount"
                            )
                          )}
                        </p>
                        <p>
                          Total {t("payment")} <br />
                          {Rupiah(
                            sumByCustomerKey(
                              item.customer_id,
                              billdet,
                              "billdet_si_total_payment"
                            ) +
                              sumByCustomerKey(
                                item.customer_id,
                                billdet,
                                "billdet_si_total_allocate"
                              )
                          )}
                        </p>
                      </IonText>
                    </IonItem>
                  </IonCardContent>
                </IonCard>
              );
            })}
          </IonList>
        ) : (
          loaded && (
            <IonImg className="img-empty" src="assets/img/search-empty.png" />
          )
        )}
      </IonContent>
    </IonPage>
  );
};

export default Bill;
